import { NgTemplateOutlet } from '@angular/common';
import { Component, Input, ViewChild } from '@angular/core';
import { CompanyProductsListComponent } from 'components/company/company-products-list.component';
import { CustomInputComponent } from 'components/input/custom-input/custom-input.component';
import { DateInputComponent } from 'components/input/date-input/date-input.component';
import { ReasonSelectComponent } from 'components/input/reason-select/reason-select.component';
import { SubmitButtonComponent } from 'components/input/submit-button/submit-button.component';
import { CredentialType } from 'enums/CredentialType';
import { InputType } from 'enums/InputType';
import { ReasonType } from 'enums/ReasonType';
import { AccountingPlansLedgerPostsAmounts } from 'models/AccountingPlansLedgerPostsAmounts';
import { ProductPlanModel } from 'models/generated/Entities.GormCrm.ProductPlan';
import { ReasonModel } from 'models/generated/Entities.GormCrm.Reason';
import { SettlementModel } from 'models/generated/Entities.GormCrm.Settlement';
import { SettlementProductPlanModel } from 'models/generated/Entities.GormCrm.SettlementProductPlan';
import { Layout } from 'modules/layout';
import { ProductNamePipe } from 'pipes/product-name.pipe';
import { TranslatePipe } from 'pipes/translate.pipe';
import { AccountingService } from 'services/accounting.service';
import { CompanyDataProvider, CompanyDataProviderFactory } from 'services/company-data-provider';
import { CredentialRequired } from 'services/credential.service';
import { DateService } from 'services/date.service';
import { FormValidationService } from 'services/form-validation.service';
import { NavigationService } from 'services/navigation.service';
import { SettlementService } from 'services/settlement.service';
import { SettlementProductPlansComponent } from './settlement-product-plans.component';
import { SettlementComponent } from './settlement.component';

enum SettlementSteps {
	Loading,
	SelectPlans,
	ConfirmSelection,
	Reason,
	Comment,
	StepThroughPlans,
	ChooseInstallments,
	SetInstallments,
	SetDueDate,
	ConfirmSettlement,

	// Error states
	NotAllowedUnfinishedSettlement
}

@Component({
	selector: 'create-settlement',
	templateUrl: './create-settlement.component.html',
	providers: [FormValidationService, CompanyDataProviderFactory],
	imports: [Layout, CompanyProductsListComponent, NgTemplateOutlet, ReasonSelectComponent, CustomInputComponent, SettlementProductPlansComponent, DateInputComponent, SettlementComponent, TranslatePipe, ProductNamePipe]
})
@CredentialRequired(CredentialType.EditSettlements)
export class CreateSettlementComponent {

	@ViewChild('settlementProductPlans') settlementProductPlans!: SettlementProductPlansComponent;
	@ViewChild('nextPlanButton') nextPlanButton?: SubmitButtonComponent;
	
	private _companyId = '';

	dataProvider?: CompanyDataProvider;
	reasonType = ReasonType;
	inputType = InputType;
	productPlans: ProductPlanModel[] = [];
	selectedProductPlans: ProductPlanModel[] = [];
	get selectedActiveProductPlans() {
		const selectedPlans = [...this.selectedProductPlans.filter(plan => !plan.parent)];
		const selectedChildsWithoutParent = this.selectedProductPlans.filter(plan => plan.parent && !this.selectedProductPlans.some(p => p.id == plan.parent?.id) && this.selectedProductPlans.some(p => p.id == plan.id));
		if (selectedChildsWithoutParent.length > 0) { // if there are selected child plans without the parent is selected
			selectedPlans.push(...selectedChildsWithoutParent); // add them to the selected plans
		}
		return selectedPlans;
	};
	remainingProductPlans: ProductPlanModel[] = [];
	get remainingActiveProductPlans() {
		const remainingPlans = [...this.remainingProductPlans.filter(plan => !plan.parent)];
		const remainingChildsWithoutParent = this.remainingProductPlans.filter(plan => plan.parent && !this.remainingProductPlans.some(p => p.id == plan.parent?.id) && this.remainingProductPlans.some(p => p.id == plan.id));
		if (remainingChildsWithoutParent.length > 0) { // if there are remaining child plans without the parent is remaining
			remainingPlans.push(...remainingChildsWithoutParent); // add them to the remaining plans
		}
		return remainingPlans;
	};
	activeProductPlan?: ProductPlanModel;
	activeSettlementProductPlan?: SettlementProductPlanModel;
	sortedSettlementProductPlans?: SettlementProductPlanModel[];
	steps = SettlementSteps;
	step = SettlementSteps.Loading;
	settlement = new SettlementModel();
	rootSettlementProductPlans: SettlementProductPlanModel[] = [];	
	plansAmount = new AccountingPlansLedgerPostsAmounts();

	isSelectable = (plan: ProductPlanModel) => {
		if (!plan.deliveryDate) return false;
		if (this.plansAmount.hasOpenAmount(plan.id!)) return true;
		if (this.accountingService.isActive(this.accountingService.filterActiveAccountingPlan(plan.accountingPlans))) return true;
		return false;
	}

	@Input()
	set companyId(id: string) {
		this._companyId = id;
		this.dataProvider = this.dataProviderFactory.getProviderForCompanyId(id);
		this.reset();
	}
	get companyId() {
		return this._companyId;
	}

	reset() {
		this.step = SettlementSteps.Loading;
		
		this.settlement = new SettlementModel();
		this.settlement.companyId = this.companyId;
		this.settlement.installments = 1;	

		this.dataProvider?.getAllProductPlansAndAccountingPlans().then(plans => {
			const productPlanIds = plans.map(pp => pp.id!);
			this.productPlans = plans;
			this.plansAmount = new AccountingPlansLedgerPostsAmounts();
			this.accountingService.getPlansOpenAndClosedAmounts(productPlanIds).then(amounts => {
				this.plansAmount = amounts;
				// A plan is eligable for a settlement if money is owed or it's in an active period
				this.selectedProductPlans = [...plans.filter(p => this.isSelectable(p))];
				this.step = SettlementSteps.SelectPlans;
			})
		});	
	}

	setReason(reason: ReasonModel) {
		this.settlement.reason = reason;
		this.settlement.reasonId = reason.id;
	}

	useSelectedProductPlans(checkForAllActivePlans: boolean = true) {
		this.removeUnselectedChildProductPlans(this.selectedProductPlans, this.selectedProductPlans.map(p => p.id!));
		this.remainingProductPlans = [...this.productPlans.filter(p => this.accountingService.isActive(this.accountingService.filterActiveAccountingPlan(p.accountingPlans)) && !this.selectedProductPlans.map(spp => spp.id).some(x => x == p.id))];
		if (this.remainingProductPlans.length > 0) {
			const unselectedProductPlanIds = this.productPlans.map(p => p.id!).filter(planId => this.selectedProductPlans.map(p => p.id!).indexOf(planId) == -1);
			this.removeUnselectedChildProductPlans(this.remainingProductPlans, unselectedProductPlanIds);
			this.step = SettlementSteps.ConfirmSelection;
			if (checkForAllActivePlans) return;
		}
		this.selectedProductPlans.sort((a, b) => {
			const dateA = a.autoRenewalDate ?? a.accountingPlan?.endDate;
			const dateB = b.autoRenewalDate ?? b.accountingPlan?.endDate;
			if (dateA && dateB) return dateB - dateA;
			return dateA ? -1 : dateB ? 1 : 0;
		});
		this.settlement.settlementProductPlans = this.selectedProductPlans.map(plan => {
			const settlementProductPlan = new SettlementProductPlanModel();
			settlementProductPlan.productPlan = plan;
			settlementProductPlan.productPlanId = plan.id!;
			settlementProductPlan.productPlan.accountingPlan = this.accountingService.filterActiveAccountingPlan(plan.accountingPlans);
			settlementProductPlan.amount = this.calculateEstimatedSettlementAmount(plan);
			settlementProductPlan.statusTitle = 'settlement.plan-status.not-done';
			return settlementProductPlan;
		});
		this.settlement.settlementProductPlans.filter(spp => spp.productPlan?.parent).forEach(spp => {
			const parent = this.settlement.settlementProductPlans.find(p => p.productPlan?.id == spp.productPlan?.parent?.id);
			if (parent)
				parent.childs = parent.childs ? parent.childs.concat([spp]) : [spp];
		});
		const productPlanIds = this.selectedProductPlans?.map(pp => pp.id!) ;
		this.plansAmount.filterByProductPlans(productPlanIds);
		this.rootSettlementProductPlans = this.settlement.settlementProductPlans.filter(spp => !spp.productPlan?.parent);
		const childsWithoutSelectedParents = this.settlement.settlementProductPlans.filter(spp => !this.rootSettlementProductPlans.some(rootPlan => rootPlan.productPlanId == spp.productPlanId) && this.selectedProductPlans.some(plan => plan.id == spp.productPlanId));
		if (childsWithoutSelectedParents.length > 0)
			this.rootSettlementProductPlans = this.rootSettlementProductPlans.concat(childsWithoutSelectedParents);
		this.sortedSettlementProductPlans = this.getSelectedProductPlansSorted();
		this.step = SettlementSteps.Reason;
	}

	getSelectedProductPlansSorted() {
		const allPlans: SettlementProductPlanModel[] = [];
		const addPlans = (plans: SettlementProductPlanModel[]) => {
			plans.forEach(plan => {
				allPlans.push(plan);
				if (plan.childs)
					addPlans(plan.childs);
			});
		};
		addPlans(this.rootSettlementProductPlans.filter(spp => !spp.productPlan?.parent));
		return allPlans;
	}

	removeUnselectedChildProductPlans(plans: ProductPlanModel[], selectedProductPlanIds: string[]) {
		plans.forEach(p => {
			p.inverseParent = p.inverseParent.filter(child => selectedProductPlanIds.indexOf(child.id!) >= 0)
			this.removeUnselectedChildProductPlans(p.inverseParent, selectedProductPlanIds);
		});
	}

	skipComment() {
		this.settlement.comment = '';
		this.goToNextPlan();
	}

	goToNextPlan() {
		this.step = SettlementSteps.StepThroughPlans;
		if (this.activeSettlementProductPlan) {
			this.activeSettlementProductPlan.statusColor = 'color-green-50';
			this.activeSettlementProductPlan.statusTitle = 'settlement.plan-status.done';
		}
		this.activeSettlementProductPlan = this.getNextPlan();
		if (!this.activeSettlementProductPlan)
			this.step = SettlementSteps.ChooseInstallments;
		if (this.nextPlanButton)
			this.nextPlanButton.reset();
	}

	private getNextPlan(): SettlementProductPlanModel | undefined {
		if (!this.sortedSettlementProductPlans) return;
		if (this.activeSettlementProductPlan) {
			const rootIndex = this.sortedSettlementProductPlans.findIndex(p => p.productPlanId == this.activeSettlementProductPlan!.productPlanId);
			if (rootIndex >= 0 && this.sortedSettlementProductPlans[rootIndex + 1]) {
				return this.sortedSettlementProductPlans[rootIndex + 1];
			}
		}
		else {
			return this.sortedSettlementProductPlans[0];
		}
	}

	calculateEstimatedSettlementAmount(plan: ProductPlanModel): number {

		const accountingPlan = this.accountingService.filterActiveAccountingPlan(plan.accountingPlans);
		if (!accountingPlan || !accountingPlan.startDate || !accountingPlan.endDate || accountingPlan.totalPlanValue == 0) return 0;

		let estimatedAmount = accountingPlan.totalPlanValue;                  // one-time products has settlement value of 100%
		if (plan.autoRenewable)
		{
			const today = this.dateService.today();
			const daysInPlan = Math.abs(this.dateService.getDiffInDays(accountingPlan.startDate, accountingPlan.endDate));
			const daysfromPlanStartToToday = Math.abs(this.dateService.getDiffInDays(accountingPlan.startDate, today));
			const percentageOfPassedPlanDuration = daysfromPlanStartToToday / daysInPlan;
			const percentageOfTotalPlanValueForFullPayment = .6;
			const percentageOfTotalPlanValueForDurationOfPassedPeriod = 1 - percentageOfTotalPlanValueForFullPayment;
			estimatedAmount *= (percentageOfTotalPlanValueForFullPayment + percentageOfTotalPlanValueForDurationOfPassedPeriod * percentageOfPassedPlanDuration);
		}

		estimatedAmount -= this.closedAmount(accountingPlan.id!);
		return estimatedAmount < 0 ? 0 : estimatedAmount.toDecimals(2);
	}

	closedAmount(accountingPlanId: string) {
		return this.plansAmount.closedAmount(accountingPlanId!);
	}

	goToConfirm() {
		this.settlementService.generateInstallments(this.settlement).then((installments) => {
			this.settlement.settlementInstallments = installments;
			this.step = SettlementSteps.ConfirmSettlement;
		});
		
	}

	saveSettlement() {
		this.settlementService.createSettlement(this.settlement).then(() => {
			this.navigation.closeView(this);
		});
	}

	cancel() {
		this.navigation.closeView(this);
	}

	constructor(private readonly dataProviderFactory: CompanyDataProviderFactory,
		private readonly accountingService: AccountingService, 
		private readonly settlementService: SettlementService, 
		private readonly navigation: NavigationService, 
		private readonly dateService: DateService) { }
}
